import React, {useCallback, useEffect, useMemo, useState} from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import Icon from '@/components/icons/Icon';
import {formatLuceneQuery} from '@/views/storyboard/chart_settings/formatLuceneQuery';
import ChartQueryExpandModal from '@/views/storyboard/chart_settings/ChartQueryExpandModal';
import ChartQuerySyntaxFeedback from '@/views/storyboard/chart_settings/ChartQuerySyntaxFeedback';
import {validateLuceneQuerySyntax} from '@/views/storyboard/utils/validateLuceneQuerySyntax';
import QuerySimilarWordsPopover from '@/views/search/QuerySimilarWordsPopover';
import {useQuerySimilarWordsPopover} from '@/views/search/hooks/useQuerySimilarWordsPopover';
import {useStoreActions} from 'store';
import i18n from 'i18n';

interface Props {
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    error?: string;
    isInvalid?: boolean;
}

const ChartQueryEditor = React.memo<Props>(({
    value,
    onChange,
    onBlur,
    error,
    isInvalid,
}) => {
    const [expandOpen, setExpandOpen] = useState(false);
    const setSamplesOpen = useStoreActions((actions) => actions.queryHelpModal.setQueryHelpModalOpen);
    const syntaxValidation = useMemo(() => validateLuceneQuerySyntax(value), [value]);
    const hasSyntaxIssues = syntaxValidation.issues.length > 0;

    const appendWord = useCallback((word: string) => {
        if (word.split(' ').length > 1) {
            onChange(`${value} "${word}"`);
            return;
        }

        onChange(`${value} ${word}`);
    }, [onChange, value]);

    const {
        setInputElement,
        popoverTarget,
        activeWord,
        similarWords,
        loadingSimilarWords,
        showPopover,
        existingWords,
        handleInputClick,
        handlePopoverHide,
        handleAddWord,
        handleQueryInputChange,
    } = useQuerySimilarWordsPopover({
        queryValue: value,
        onAppendWord: appendWord,
    });

    useEffect(() => {
        if (expandOpen) {
            handleQueryInputChange();
        }
    }, [expandOpen, handleQueryInputChange]);

    const handleFormat = useCallback(() => {
        onChange(formatLuceneQuery(value));
    }, [value, onChange]);

    const handleExpandApply = useCallback((newValue: string) => {
        onChange(newValue);
    }, [onChange]);

    const handleChange = useCallback((nextValue: string) => {
        handleQueryInputChange();
        onChange(nextValue);
    }, [handleQueryInputChange, onChange]);

    const showInvalid = hasSyntaxIssues || isInvalid;

    return (
        <div className="chart-query-editor">
            <label className="cs-field__label">
                {i18n.t('jt_search_query.text')}
            </label>

            <QuerySimilarWordsPopover
                target={popoverTarget}
                show={!expandOpen && showPopover}
                activeWord={activeWord ?? ''}
                similarWords={similarWords}
                loading={loadingSimilarWords}
                existingWords={existingWords}
                onHide={handlePopoverHide}
                onAddWord={handleAddWord}
            />

            <div className={`chart-query-editor__wrapper${showInvalid ? ' chart-query-editor__wrapper--invalid' : ''}`}>
                <TextareaAutosize
                    ref={setInputElement}
                    className="chart-query-editor__textarea"
                    value={value}
                    onClick={handleInputClick}
                    onChange={e => handleChange(e.target.value)}
                    onBlur={onBlur}
                    spellCheck={false}
                    placeholder="*"
                    minRows={1}
                    maxRows={6}
                />

                <div className="chart-query-editor__toolbar">
                    <span className="chart-query-editor__char-count">
                        {value.length} chars
                    </span>
                    <button
                        type="button"
                        className="chart-query-editor__action-btn"
                        onClick={handleFormat}
                    >
                        {i18n.t('cs_format_query.text')}
                    </button>
                    <button
                        type="button"
                        className="chart-query-editor__action-btn"
                        onClick={() => setExpandOpen(true)}
                    >
                        {i18n.t('cs_expand_query.text')}
                        <Icon name="mdi-arrow-top-right" size="12px" />
                    </button>
                </div>
            </div>

            {hasSyntaxIssues ? (
                <ChartQuerySyntaxFeedback query={value} />
            ) : null}

            {!hasSyntaxIssues && isInvalid && error ? (
                <div className="chart-query-editor__error">
                    {error}
                </div>
            ) : null}

            <a
                className="chart-query-editor__help"
                onClick={() => setSamplesOpen(true)}
            >
                <Icon name="mdi-open-in-new" size="1em" />
                {i18n.t('how_to_write_queries.text')}
            </a>

            <ChartQueryExpandModal
                isOpen={expandOpen}
                onClose={() => setExpandOpen(false)}
                value={value}
                onApply={handleExpandApply}
            />
        </div>
    );
});

export default ChartQueryEditor;
